import { useMemo } from 'react';

import type { ChartData, ChartFields, KpiTile } from '../../types';
import { ChartIcon } from '../icons';
import { InfoTip } from '../InfoTip';
import { Stars } from '../Stars';
import { Empty } from '../states';
import { inferTiles, resolveTiles, type ResolvedTile } from '../kpi';
import { percent } from '../format';

/**
 * A row of KPI tiles — the form that replaces the hand-written KPI fragment.
 *
 * What makes this a form rather than another bespoke block: every tile is
 * configuration (`chart.tiles`), and every piece of that configuration is
 * optional. An author who names six columns gets six finished tiles with
 * inferred icons, colours and number formats; an author who configures nothing
 * gets tiles inferred from the result, so the form is never a blank card.
 *
 * Each tile reads its OWN source frame when the chart is composite. That is the
 * structural difference from the fragment it replaces: one dead source costs
 * one tile, not the whole strip. A tile whose source came back empty says so in
 * its own space and its neighbours carry on.
 */

interface Props {
  data: ChartData;
  fields?: ChartFields;
  /** Author's tile configuration; absent infers tiles from the result. */
  tiles?: KpiTile[];
  /** A composite chart's named source frames, one per tile. */
  datasets?: Record<string, ChartData>;
}

const Delta = ({ change, caption }: { change: number | null; caption?: string }) => {
  // No comparison value is not "no change" — a 0% with a flat marker would be a
  // claim we cannot support. The caption still shows, since "all-time" is worth
  // saying on a tile that has no delta by nature.
  if (change === null) {
    return caption ? <div className="kpi__delta kpi__delta--bare">{caption}</div> : null;
  }

  const direction = change > 0 ? 'up' : change < 0 ? 'down' : 'flat';

  return (
    <div className="kpi__delta" data-direction={direction}>
      <span aria-hidden="true">{change > 0 ? '▲' : change < 0 ? '▼' : '■'}</span>
      <span>{percent(Math.abs(change))}</span>
      {caption && <span className="kpi__caption">{caption}</span>}
    </div>
  );
};

const KpiCard = ({ tile }: { tile: ResolvedTile }) => (
  <div className="kpi">
    {tile.icon && <ChartIcon name={tile.icon} tone={tile.tone} />}

    <div className="kpi__label">
      <span>{tile.label}</span>
      <InfoTip text={tile.description} about={tile.label} />
    </div>

    {tile.rating ? (
      <div className="kpi__value">
        <Stars value={tile.rating.value} max={tile.rating.max} />
        <span className="kpi__value-num">{tile.display}</span>
      </div>
    ) : (
      <div className="kpi__value" title={tile.exactTitle}>
        {tile.missing ? <span className="kpi__value-missing">No data</span> : tile.display}
      </div>
    )}

    <Delta change={tile.change} caption={tile.caption} />
  </div>
);

export const KpiRow = ({ data, fields, tiles, datasets }: Props) => {
  const resolved = useMemo(
    () =>
      tiles?.length ? resolveTiles(tiles, datasets ?? {}, data) : inferTiles(data, fields),
    [tiles, datasets, data, fields],
  );

  if (!resolved.length) return <Empty />;

  // The count drives the grid: six tiles want six columns, three want three.
  // Letting CSS auto-fit instead would leave a 4-tile row as 3 + 1 orphan.
  return (
    // The shell is the query container — the grid cannot query its own width.
    <div className="kpi-shell">
      <div className="kpis" data-count={Math.min(resolved.length, 8)}>
        {resolved.map((tile) => (
          <KpiCard key={tile.id} tile={tile} />
        ))}
      </div>
    </div>
  );
};
